import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight, ExternalLink, Undo2 } from "lucide-react";
import { useCreate, useGetIdentity, useGetList, useNotify } from "ra-core";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { Explain } from "./Explain";
import { MARKETING_PATHS } from "./paths";

/**
 * Triage queue — the review workflow, moved out of a generated HTML file.
 *
 * WHAT THIS DOES NOT DO
 * ---------------------
 * It does not decide what to ask. Candidates and their evidence come from
 * `review_queue`, populated by pipeline/sync_review_queue.py, which imports
 * make_review_page.build_decisions() — 1,530 lines built on the validated
 * street grammar. None of that is reimplemented here.
 *
 * It also does not write overrides. It records SEMANTICS into `decisions`
 * (type + answer + value), exactly as the HTML page did. apply_decisions.py
 * owns the mapping from a decision to override syntax, so that vocabulary
 * lives in one place. Writing `overrides` rows from TypeScript would put a
 * second copy of that mapping in a second language.
 *
 * WHY KEYBOARD-FIRST
 * ------------------
 * The page this replaces was keyboard-driven and 258 decisions were made with
 * it. A prettier replacement that needs a mouse would be a downgrade. Every
 * verdict is a single keystroke; nothing needs pointing at.
 *
 * WHY NO STATUS WRITE
 * -------------------
 * `review_backlog` excludes any item that already has a decision with the same
 * key, so recording the decision retires the question by itself. One write, and
 * no window where a decision exists but the queue still shows it.
 */

type ReviewItem = {
  id: number;
  item_key: string;
  type: "merge" | "host" | "supp" | "lookup" | "family";
  question: string;
  verdict_hint: string | null;
  why: string | null;
  payload: Record<string, unknown>;
  zip: string | null;
  house_number: string | null;
  permit_number: string | null;
  city: string | null;
  state: string | null;
  address_ref: number | null;
  first_seen: string | null;
  last_seen: string | null;
  /** Days this question has been waiting. From review_backlog, migration 027. */
  days_open: number | null;
  /** Rulings already made about THIS DOOR, not this question. */
  door_prior_decisions: number;
  door_last_decided_on: string | null;
  /** Is the permit AT THIS ADDRESS a Distilled Spirits Plant? False on every
   *  currently open item -- all 78 DSP questions were answered, so what is left
   *  is the non-DSP tail. */
  is_dsp: boolean;
  permit_classes: string;
  segment: string | null;
  place_kind: string | null;
  operator_name: string | null;
  /** The OPERATOR's whole footprint. May include a DSP even when this address
   *  is not one -- a distillery reviewed at its office is a different
   *  judgment from a pure importer. */
  operator_kinds: string;
  operator_permits: number | null;
  operator_doors: number | null;
  rated_potential: "high" | "low" | null;
  rated_account_type: string | null;
};

/** An answer the pipeline's to_rows() understands. Keep in step with
 *  apply_decisions.py — these strings are the contract between the two. */
type Choice = {
  key: string;
  label: string;
  answer: string;
  /** needs the free-text field filled before it can be submitted */
  needsValue?: boolean;
  tone?: "default" | "destructive" | "outline" | "secondary";
};

/**
 * "It holds a distilling permit but is not a distillery."
 *
 * THIS EXISTED, AND THIS SCREEN DROPPED IT.
 * -----------------------------------------
 * The HTML review page bound it to `Z`, apply_decisions.to_rows() has always
 * understood `not_prospect`, and 41 decisions in the table were made with it --
 * the third most common verdict on record. When review moved into the CRM it
 * came across with two answers per question type and this was not one of them,
 * so a verdict the pipeline still reads became unreachable from the only screen
 * that can produce one.
 *
 * WHY IT IS NOT JUST "SUPPRESS"
 * A retail liquor store, a licensing shell and a plain office all hold real DSP
 * permits and all arrive looking like prospects. `suppress` and `not_prospect`
 * both stop the card; they differ in what they claim, and that difference is
 * the whole value a year later when the door comes back round. `defunct` says
 * the site is GONE. This says the site is THERE and was never a customer --
 * which is also why it does not expire: a liquor store does not become a
 * distillery, so re-asking is waste.
 *
 * Worked example from the archive: BLUE DRAGON SPIRITS LLC, Green Cove Springs
 * FL, holds FL-S-20066 (Distilled Spirits Plant), and is a retail liquor store.
 *
 * It is appended to EVERY type rather than listed in each, because it answers a
 * question about the COMPANY and every queue is looking at one. Declaring it
 * once also means a new question type cannot accidentally ship without it --
 * which is exactly how it went missing the first time.
 */
const NOT_A_DISTILLERY: Choice = {
  key: "3",
  label: "Not a distillery",
  answer: "not_prospect",
  tone: "outline",
};

const CHOICES: Record<ReviewItem["type"], Choice[]> = {
  merge: [
    { key: "1", label: "One door", answer: "merge" },
    {
      key: "2",
      label: "Two streets",
      answer: "keep separate",
      tone: "outline",
    },
  ],
  supp: [
    { key: "1", label: "Mail it", answer: "mail it" },
    { key: "2", label: "Suppress", answer: "suppress", tone: "destructive" },
  ],
  family: [
    {
      key: "1",
      label: "Same company",
      answer: "same company",
      needsValue: true,
    },
    {
      key: "2",
      label: "Different companies",
      answer: "different companies",
      tone: "outline",
    },
  ],
  host: [
    { key: "1", label: "Use selected", answer: "host", needsValue: true },
    { key: "2", label: "Defunct", answer: "defunct", tone: "destructive" },
  ],
  lookup: [
    { key: "1", label: "Save address", answer: "address", needsValue: true },
    { key: "2", label: "Closed / gone", answer: "closed", tone: "destructive" },
  ],
};

/**
 * The queues, in the order the HTML review page used.
 *
 * That order is not alphabetical and is not arbitrary: it runs from the
 * questions that unblock others (a missing address changes what can be merged)
 * to the ones that only affect a single card. Working top to bottom does the
 * least re-deciding.
 */
const QUEUES: [ReviewItem["type"], string][] = [
  ["lookup", "Address lookups"],
  ["merge", "Street merges"],
  ["host", "Host picks"],
  ["supp", "Suppressions"],
  ["family", "Owner families"],
];

const QUEUE_LABEL: Record<string, string> = Object.fromEntries([
  ["all", "Everything"],
  ...QUEUES,
]);

const TYPE_LABEL: Record<ReviewItem["type"], string> = {
  merge: "Merge",
  host: "Host",
  supp: "Suppression",
  lookup: "Lookup",
  family: "Owner family",
};

/**
 * One reviewable place: who is there, then where it is.
 *
 * NAME FIRST. The question is almost always about the BUSINESS -- is this the
 * same company, should we mail it, who goes on the card -- and the address is
 * how you locate it. Leading with the street made every card look alike and
 * buried the thing being judged.
 *
 * THE ADDRESS IS PRINTED WHOLE. "300 S LEWIS DR" is not answerable; "300 S
 * LEWIS DR, CAMARILLO, CA 93012" is, because deciding whether two records are
 * one door or a mail drop needs the town. The street stays in the foreground
 * color because it is the part under review; the city, state and ZIP are
 * context and are muted, so the eye lands on the part that differs between
 * two candidates rather than re-reading the town each time.
 */
const Place = ({
  name,
  street,
  city,
  state,
  zip,
  aside,
}: {
  name?: string | null;
  street?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  aside?: ReactNode;
}) => {
  const tail = [city, [state, zip].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");
  return (
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="text-sm font-medium">{name || "—"}</div>
        <div className="text-sm font-mono">
          <span>{street || "—"}</span>
          {tail ? (
            <span className="text-muted-foreground">, {tail}</span>
          ) : null}
        </div>
      </div>
      {aside}
    </div>
  );
};

const mapsUrl = (parts: (string | null | undefined)[]) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    parts.filter(Boolean).join(" "),
  )}`;

const asArray = (v: unknown): Record<string, unknown>[] =>
  Array.isArray(v) ? (v as Record<string, unknown>[]) : [];

const asStrings = (v: unknown): string[] =>
  Array.isArray(v) ? v.map(String) : [];

/** The evidence panel. Each type carries genuinely different fields, which is
 *  why the payload is JSONB rather than a flattened column set. */
/**
 * Rate the thing being reviewed, without answering the question.
 *
 * WHY IT IS KEYED ON THE QUESTION AND NOT A DOOR
 * ----------------------------------------------
 * `rate_door` needs an address, and a merge question has none: 49 of them carry
 * no address_ref and their zip+house_number matches nothing in `addresses`,
 * because the permits are not DSPs and were never on the mailing list. Keying a
 * rating to a door would make it work everywhere except where it is most
 * needed.
 *
 * A rating is NOT a decision. It records judgment about whether an operator is
 * worth pursuing; the decision below records what to do about the address. They
 * are stored separately -- prospects and decisions -- and neither retires the
 * other, so rating an item leaves it in the queue.
 */
const RateItem = ({
  item,
  onRated,
}: {
  item: ReviewItem;
  onRated: () => void;
}) => {
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  const [potential, setPotential] = useState(item.rated_potential ?? "unrated");
  const [accountType, setAccountType] = useState(
    item.rated_account_type ?? "unrated",
  );

  // The queue advances under this component, so the controls follow the item
  // rather than keeping the previous one's answer.
  useEffect(() => {
    setPotential(item.rated_potential ?? "unrated");
    setAccountType(item.rated_account_type ?? "unrated");
  }, [item.id, item.rated_potential, item.rated_account_type]);

  const save = async (nextPotential: string, nextType: string) => {
    setBusy(true);
    try {
      const { getSupabaseClient } = await import(
        "../providers/supabase/supabase"
      );
      const { error } = await getSupabaseClient().rpc("rate_review_item", {
        p_item_key: item.item_key,
        // "unrated" is the UI's word for null; sending the literal string would
        // fail rate_review_item's vocabulary check, which is the point of it.
        p_potential: nextPotential === "unrated" ? null : nextPotential,
        p_account_type: nextType === "unrated" ? null : nextType,
        p_note: null,
      });
      if (error) throw new Error(error.message);
      onRated();
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-end gap-2 border rounded-md p-2">
      <div>
        <div className="text-xs text-muted-foreground mb-1">
          <Explain term="potential">Potential</Explain>
        </div>
        <Select
          value={potential}
          disabled={busy}
          onValueChange={(v) => {
            setPotential(v);
            save(v, accountType);
          }}
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="unrated">Not rated</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <div className="text-xs text-muted-foreground mb-1">Type</div>
        <Select
          value={accountType}
          disabled={busy}
          onValueChange={(v) => {
            setAccountType(v);
            save(potential, v);
          }}
        >
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="distiller">distiller</SelectItem>
            <SelectItem value="bottler_ndp">bottler / NDP</SelectItem>
            <SelectItem value="other">other</SelectItem>
            <SelectItem value="na">not applicable</SelectItem>
            <SelectItem value="unrated">unrated</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <p className="text-xs text-muted-foreground basis-full">
        Saved as you change it. A rating is not an answer — the item stays in
        the queue until you decide below.
      </p>
    </div>
  );
};

const Evidence = ({ item }: { item: ReviewItem }) => {
  const p = item.payload ?? {};

  if (item.type === "merge") {
    const legs = asArray(p.legs);
    return (
      <div className="space-y-2">
        {legs.map((leg, i) => (
          <div key={i} className="border rounded-md p-3">
            <Place
              name={String(leg.companies ?? "—")}
              street={String(leg.street ?? "")}
              city={item.city}
              state={item.state}
              zip={item.zip}
              aside={
                <a
                  href={mapsUrl([
                    String(leg.street ?? ""),
                    item.city,
                    item.state,
                    item.zip,
                  ])}
                  target="_blank"
                  rel="noreferrer"
                  className="text-muted-foreground hover:text-foreground shrink-0"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              }
            />
          </div>
        ))}
      </div>
    );
  }

  if (item.type === "host") {
    const ents = asStrings(p.entities);
    const scores = (p.scores ?? {}) as Record<string, string>;
    const reasons = (p.reasons ?? {}) as Record<string, string>;
    return (
      <div className="space-y-1">
        {ents.map((e) => (
          <div
            key={e}
            className="border rounded-md p-2 flex justify-between gap-3"
          >
            <span className="text-sm">{e}</span>
            <span className="text-xs text-muted-foreground shrink-0">
              {scores[e] ? `score ${scores[e]}` : ""}
              {reasons[e] ? ` · ${reasons[e]}` : ""}
            </span>
          </div>
        ))}
      </div>
    );
  }

  if (item.type === "family") {
    return (
      <div className="space-y-1">
        {asStrings(p.names).map((n) => (
          <div key={n} className="border rounded-md p-2 text-sm">
            {n}
          </div>
        ))}
        <div className="text-xs text-muted-foreground pt-1">
          {asStrings(p.permits).join("  ")}
        </div>
      </div>
    );
  }

  // supp and lookup. `occupants` is present only where several permit holders
  // share one building — sync_review_queue.collapse() merges those into a
  // single question rather than asking it once per suite.
  const occupants = asArray(p.occupants);
  return (
    <div className="space-y-2">
      <div className="border rounded-md p-3">
        <Place
          name={String(p.name ?? "—") + (p.kind ? ` · ${String(p.kind)}` : "")}
          street={String(p.street ?? "")}
          city={item.city}
          state={item.state}
          zip={item.zip}
          aside={
            <a
              href={mapsUrl([
                String(p.street ?? ""),
                item.city,
                item.state,
                item.zip,
              ])}
              target="_blank"
              rel="noreferrer"
              className="text-muted-foreground hover:text-foreground shrink-0"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          }
        />
      </div>
      {occupants.length > 1 && (
        <div className="border rounded-md p-3">
          <div className="text-xs text-muted-foreground mb-2">
            {occupants.length} permit holders at this address
          </div>
          {occupants.map((o, i) => (
            <div key={i} className="py-1">
              <Place
                name={String(o.name ?? "—")}
                street={String(o.street ?? "")}
                city={item.city}
                state={item.state}
                zip={item.zip}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export function ReviewQueue() {
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const [create] = useCreate();

  const [cursor, setCursor] = useState(0);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [queue, setQueue] = useState<"all" | ReviewItem["type"]>("all");
  /**
   * Non-DSP items are hidden by default, restoring a gate that was lost in the
   * move off the HTML page.
   *
   * `make_review_page.py` has always had `const relevant = d => showRetail ||
   * d.dsp`, and prints at build time: "N touch a distillery, M retail-only
   * (hidden by default)". `sync_review_queue.py` writes the raw output of
   * build_decisions() -- every candidate -- so the filter stayed in the page's
   * JavaScript and never reached the database.
   *
   * The CRM then displayed all of it faithfully, because nothing in the data
   * said otherwise. 199 out-of-scope questions read as a backlog for weeks.
   *
   * Hidden, not discarded: the old page offered the same toggle, and a
   * retail-only permit is still a fact somebody may want to look at.
   */
  const [showRetail, setShowRetail] = useState(false);
  const [lastDecision, setLastDecision] = useState<{
    id: number;
    question: string;
    answer: string;
  } | null>(null);

  const { data, total, isPending, refetch } = useGetList<ReviewItem>(
    "review_backlog",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "id", order: "ASC" },
    },
  );

  const all = useMemo(() => data ?? [], [data]);

  /**
   * Filtered in the browser rather than by re-querying.
   *
   * The whole backlog is a few hundred rows and is already loaded, so a queue
   * switch is instant and the counts beside each queue stay honest without a
   * round trip per tab. It also means answering an item updates every count at
   * once, which a per-queue query would not.
   */
  const items = useMemo(() => {
    const gated = showRetail ? all : all.filter((d) => d.is_dsp);
    return queue === "all" ? gated : gated.filter((d) => d.type === queue);
  }, [all, queue, showRetail]);

  /** What the gate is holding back, so it is never silently swallowing work. */
  const hiddenCount = useMemo(
    () => (showRetail ? 0 : all.filter((d) => !d.is_dsp).length),
    [all, showRetail],
  );

  // Counted over what the gate ACTUALLY shows. Counting the ungated set would
  // put "Suppressions 149" on a tab that opens onto an empty list -- the exact
  // contradiction the gate exists to remove.
  const counts = useMemo(() => {
    const visible = showRetail ? all : all.filter((d) => d.is_dsp);
    const c: Record<string, number> = { all: visible.length };
    for (const d of visible) c[d.type] = (c[d.type] ?? 0) + 1;
    return c;
  }, [all, showRetail]);

  // Changing queue -- or the gate -- must not leave the cursor beyond the end
  // of a shorter list.
  useEffect(() => {
    setCursor(0);
  }, [queue, showRetail]);

  const item = items[cursor];
  // Memoized: the keydown effect depends on this, and a fresh array each
  // render would tear down and re-register the global listener continuously.
  // Appended here rather than in CHOICES so the per-type table stays a
  // statement about that type's own question, and the verdict that applies to
  // all of them is visibly universal. Everything downstream -- the keyboard
  // handler, the buttons -- reads `choices`, so both pick it up with no
  // further change.
  const choices = useMemo(
    () => (item ? [...CHOICES[item.type], NOT_A_DISTILLERY] : []),
    [item],
  );

  // Reset the free-text field whenever the question changes, so an address
  // typed for one door can never be submitted against the next.
  useEffect(() => {
    setValue("");
  }, [item?.id]);

  // Host decisions default to the pipeline's own pick, which is right most of
  // the time; the reviewer overrides it when the evidence says otherwise.
  useEffect(() => {
    if (item?.type === "host") {
      const chosen = (item.payload?.chosen ?? item.payload?.addressee) as
        | string
        | undefined;
      if (chosen) setValue(chosen);
    }
  }, [item?.id, item?.type, item?.payload]);

  const submit = useCallback(
    async (choice: Choice) => {
      if (!item || saving) return;
      if (choice.needsValue && !value.trim()) {
        notify(`${choice.label} needs a value first`, { type: "warning" });
        return;
      }
      setSaving(true);
      try {
        const created = await create(
          "decisions",
          {
            data: {
              decided_on: new Date().toISOString().slice(0, 10),
              decision_key: item.item_key,
              type: item.type,
              // The exact strings apply_decisions.to_rows() matches on. `host`
              // is a special case there: the answer is not the literal word,
              // the company name in `value` is what carries the decision.
              answer: choice.answer === "host" ? value.trim() : choice.answer,
              value: choice.needsValue ? value.trim() : null,
              // THE DOOR THIS IS ABOUT. Omitting it was a real loss: a decision
              // made here did not attach to any address, so the door's own page
              // could not show that anyone had ever ruled on it. Only 97 of 259
              // existing decisions carry one, and those came from the pipeline.
              address_ref: item.address_ref ?? null,
              // THE AUTHOR, as a column rather than a sentence. The name used to
              // go only into `note` as prose -- unqueryable, unjoinable, and
              // wrong the moment anyone edited the note.
              decided_by: identity?.id ?? null,
              note: `reviewed in CRM by ${identity?.fullName ?? "staff"}`,
              match_status: "unmatched",
            },
          },
          { returnPromise: true },
        );
        // Remembered so it can be taken back. Only the LAST one: an undo stack
        // over a queue that renumbers as it drains would be a way to delete a
        // decision you can no longer see, which is worse than no undo at all.
        setLastDecision({
          id: Number((created as { id?: number | string })?.id),
          question: item.question,
          answer: choice.label,
        });
        // "Not a distillery" also SETS THE RATING, because that is what it
        // means: potential `low`, account type `na`. The HTML page did this and
        // it is not a convenience -- a decision recorded without it leaves the
        // operator reading `unrated` forever, so the door's own page shows a
        // suppression with no stated commercial judgment behind it.
        //
        // Deliberately after the decision and deliberately not fatal. The
        // decision is the record that stops the card; the rating is commentary
        // on it. Failing the whole submit because the commentary did not save
        // would lose the answer the reviewer actually gave.
        if (choice.answer === "not_prospect") {
          try {
            const { getSupabaseClient } = await import(
              "../providers/supabase/supabase"
            );
            const { error } = await getSupabaseClient().rpc(
              "rate_review_item",
              {
                p_item_key: item.item_key,
                p_potential: "low",
                p_account_type: "na",
                p_note: null,
              },
            );
            if (error) throw new Error(error.message);
          } catch (e) {
            notify(
              `Recorded "not a distillery", but the rating did not save: ${
                (e as Error).message
              }`,
              { type: "warning" },
            );
          }
        }
        // Advance rather than refetch: refetching would renumber the list under
        // the cursor and make rapid triage lose its place.
        setCursor((c) => Math.min(c + 1, items.length - 1));
      } catch (e) {
        notify(`Could not record decision: ${(e as Error).message}`, {
          type: "error",
        });
      } finally {
        setSaving(false);
      }
    },
    [item, value, saving, create, identity, notify, items.length],
  );

  /**
   * Take back the answer just given.
   *
   * Deleting the decision IS the undo, because the queue is derived: with the
   * row gone, `review_backlog` stops filtering the question out and it returns
   * on its own. Nothing has to un-retire it, since nothing ever retired it.
   *
   * undo_decision() refuses an already-applied decision -- the override the
   * pipeline wrote from it would survive the delete, so the question would come
   * back over a ruling that still stands. That refusal is the whole reason this
   * is an RPC and not a delete from here.
   */
  const undo = useCallback(async () => {
    if (!lastDecision) return;
    try {
      const { getSupabaseClient } = await import(
        "../providers/supabase/supabase"
      );
      const { error } = await getSupabaseClient().rpc("undo_decision", {
        p_decision_id: lastDecision.id,
      });
      if (error) throw new Error(error.message);
      notify(`Undone. "${lastDecision.question}" is back in the queue.`, {
        type: "info",
      });
      setLastDecision(null);
      // Step back to where the answer was given, so the question you just
      // restored is the one in front of you.
      setCursor((c) => Math.max(0, c - 1));
      refetch();
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    }
  }, [lastDecision, notify, refetch]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Never steal keys from the address field.
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA")) {
        if (e.key === "Enter") {
          const c = choices.find((x) => x.needsValue);
          if (c) submit(c);
        }
        return;
      }
      if (e.key === "j" || e.key === "ArrowDown" || e.key === "ArrowRight") {
        setCursor((c) => Math.min(c + 1, items.length - 1));
      } else if (
        e.key === "k" ||
        e.key === "ArrowUp" ||
        e.key === "ArrowLeft"
      ) {
        setCursor((c) => Math.max(c - 1, 0));
      } else if (e.key === "s") {
        setCursor((c) => Math.min(c + 1, items.length - 1));
      } else {
        const c = choices.find((x) => x.key === e.key);
        if (c) submit(c);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [choices, items.length, submit]);

  const QueueBar = () => (
    <div className="flex gap-1 overflow-x-auto pb-1">
      {([["all", "Everything"], ...QUEUES] as [string, string][]).map(
        ([key, label]) => {
          const n = counts[key] ?? 0;
          // A queue with nothing left is shown, not hidden: "Suppressions 0"
          // is information -- it says that queue is finished.
          return (
            <button
              key={key}
              type="button"
              onClick={() => setQueue(key as "all" | ReviewItem["type"])}
              className={cn(
                "flex items-center gap-1.5 rounded px-2.5 py-1 text-xs whitespace-nowrap border transition-colors",
                queue === key
                  ? "border-foreground font-medium"
                  : "border-transparent text-muted-foreground hover:text-foreground",
                !n && queue !== key ? "opacity-50" : null,
              )}
            >
              {label}
              <span className="tabular-nums opacity-60">{n}</span>
            </button>
          );
        },
      )}

      {/*
        The hidden count, mirroring the HTML page's own `hiddenCount()`.

        A gate that filters silently is worse than no gate: it turns "there is
        nothing to do" and "you are not being shown anything" into the same
        screen. This says which, and offers the way through.
      */}
      {(hiddenCount > 0 || showRetail) && (
        <button
          type="button"
          onClick={() => setShowRetail((s) => !s)}
          className="ml-auto shrink-0 rounded px-2.5 py-1 text-xs whitespace-nowrap border border-transparent text-muted-foreground hover:text-foreground"
          title="These permits are not Distilled Spirits Plants, so they are not doors this campaign mails. The old HTML review page hid them by default too."
        >
          {showRetail
            ? "hide non-distillery items"
            : `${hiddenCount} hidden — not a distillery`}
        </button>
      )}
    </div>
  );

  /**
   * Rendered in BOTH return paths, which is why it is a variable rather than
   * inline JSX. The empty state is the case that matters most: answering the
   * last question drains the queue, so the moment you are most likely to want
   * an undo is the moment the screen has nothing else on it.
   */
  const undoBar = lastDecision ? (
    // Above the queue rather than beside the answer keys: it refers to the
    // question you have just left, not the one on screen, and putting it next
    // to the keys would invite undoing the wrong thing.
    <div className="flex flex-wrap items-center gap-2 rounded-md border px-3 py-2">
      <span className="text-sm">
        Answered <b>{lastDecision.answer}</b> to &ldquo;{lastDecision.question}
        &rdquo;
      </span>
      <Button variant="outline" size="sm" onClick={undo}>
        <Undo2 className="size-4" /> Undo
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setLastDecision(null)}
        className="text-muted-foreground"
      >
        Dismiss
      </Button>
    </div>
  ) : null;

  if (isPending) {
    return (
      <div className="p-6 space-y-3">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (!item) {
    /**
     * An empty queue still renders the queue bar.
     *
     * Without it, opening a queue with nothing in it removed the only way back
     * out and the tab had to be reloaded. An empty state that strands you is
     * worse than no empty state -- and a finished queue is a NORMAL thing to
     * click, since the counts invite it.
     */
    const done = queue !== "all" && (counts.all ?? 0) > 0;
    return (
      <div className="py-4 space-y-4">
        <QueueBar />
        {undoBar}
        <div className="p-12 text-center border rounded-lg">
          <p className="text-lg font-medium">
            {done
              ? `Nothing left in ${QUEUE_LABEL[queue] ?? queue}.`
              : "Nothing to review."}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            {done ? (
              <>{counts.all} still open elsewhere — pick another queue above.</>
            ) : (
              <>
                Run <code>pipeline/sync_review_queue.py</code> after a rebuild
                to load new candidates.
              </>
            )}
          </p>
          {done ? (
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => setQueue("all")}
            >
              Back to everything
            </Button>
          ) : null}
        </div>
      </div>
    );
  }

  const needsValue = choices.some((c) => c.needsValue);

  return (
    <div className="py-4 space-y-4">
      <QueueBar />
      {undoBar}

      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Badge variant="secondary">{TYPE_LABEL[item.type]}</Badge>
          {item.verdict_hint && (
            <Badge variant="outline">{item.verdict_hint}</Badge>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Previous"
            disabled={cursor === 0}
            onClick={() => setCursor((c) => Math.max(0, c - 1))}
          >
            <ChevronLeft className="size-4" />
          </Button>
          <span className="text-sm text-muted-foreground tabular-nums">
            {cursor + 1} of {items.length}
            {queue === "all" && total && total !== items.length
              ? ` (${total})`
              : ""}
          </span>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Next"
            disabled={cursor >= items.length - 1}
            onClick={() => setCursor((c) => Math.min(items.length - 1, c + 1))}
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>
      </div>

      <div>
        <h2 className="text-xl font-semibold">{item.question}</h2>
        {item.why && (
          <p className="text-sm text-muted-foreground mt-1">{item.why}</p>
        )}
        <p className="text-xs text-muted-foreground mt-1 font-mono">
          {item.item_key}
          {item.city ? ` · ${item.city}, ${item.state} ${item.zip}` : ""}
        </p>

        {/*
          IS THIS FRESH, OR HAS THIS DOOR BEEN HERE BEFORE?

          The queue only ever shows UNDECIDED questions, so a door ruled on
          last month and a door never seen before looked identical. They are
          not: a new question about an already-examined door usually means the
          pipeline re-raised something a person already settled.

          THE THIRD STATE IS THE IMPORTANT ONE. Only 1 of 198 open questions is
          linked to an address at all -- sync_review_queue only links when the
          door is still mailable AND exactly one address matches the zip and
          house number, which by construction excludes suppression candidates
          and the ambiguous buildings that merge questions are about.

          So "no prior decisions" and "we cannot tell" must not render the same.
          Saying "first time this door has come up" about a question with no
          door link would be a confident claim built on a missing join.
        */}
        {/*
          WHAT IS THIS, BEFORE WHETHER TO MERGE IT.

          The question arrived with no way to tell whether the streets belong to
          a distillery or to an importer nobody intends to mail. Every open item
          reads dsp = false while all 78 answered ones read true -- the split is
          exact, so the DSP review is finished and what remains is out of scope.
          That fact was in the payload the whole time and on screen nowhere.
        */}
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <Badge
            variant={item.is_dsp ? "secondary" : "outline"}
            className={item.is_dsp ? "" : "border-orange-400"}
          >
            {item.is_dsp
              ? "Distilled Spirits Plant"
              : "not a distillery at this address"}
          </Badge>
          {item.permit_classes && (
            <Badge variant="outline">{item.permit_classes}</Badge>
          )}
          {item.segment && <Badge variant="outline">{item.segment}</Badge>}
          {item.place_kind && (
            <Badge variant="outline">{item.place_kind}</Badge>
          )}
        </div>

        {(item.operator_name || item.operator_kinds) && (
          <p className="text-xs text-muted-foreground mt-2">
            {item.operator_name && <b>{item.operator_name}</b>}
            {item.operator_kinds && (
              <>
                {item.operator_name ? " — " : ""}
                holds {item.operator_kinds}
              </>
            )}
            {item.operator_permits != null && (
              <>
                {" "}
                · {item.operator_permits} permit
                {item.operator_permits === 1 ? "" : "s"}
              </>
            )}
            {item.operator_doors != null && (
              <>
                {" "}
                across {item.operator_doors} door
                {item.operator_doors === 1 ? "" : "s"}
              </>
            )}
            {/* The subtle case, and the one worth naming: the OPERATOR is a
                distillery while THIS ADDRESS is their office or wholesale arm.
                That is exactly the judgment the question is asking for. */}
            {!item.is_dsp &&
              item.operator_kinds.includes("Distilled Spirits") && (
                <>
                  {" "}
                  — the operator <b>is</b> a distillery; this address is not the
                  plant.
                </>
              )}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-2 mt-2">
          {item.first_seen && (
            <Badge variant="outline">
              in the queue since {item.first_seen}
              {item.days_open ? ` · ${item.days_open}d` : ""}
            </Badge>
          )}
          {item.address_ref == null ? (
            <Badge
              variant="outline"
              className="text-muted-foreground"
              title="sync_review_queue links a question to a door only when the door is still mailable and exactly one address matches its zip and house number"
            >
              not linked to a door — history unknown
            </Badge>
          ) : item.door_prior_decisions > 0 ? (
            <>
              <Badge variant="outline" className="border-orange-400">
                this door already has {item.door_prior_decisions} decision
                {item.door_prior_decisions === 1 ? "" : "s"}
                {item.door_last_decided_on
                  ? ` · last ${item.door_last_decided_on}`
                  : ""}
              </Badge>
              <Link
                to={`${MARKETING_PATHS.doors}/${item.address_ref}`}
                className="text-xs underline"
                target="_blank"
                rel="noreferrer"
                // A new tab on purpose: opening the history in place would lose
                // the reviewer's position in the queue, and triage is done at
                // speed.
              >
                see its history
              </Link>
            </>
          ) : (
            <Badge variant="outline">no decision recorded for this door</Badge>
          )}
        </div>
      </div>

      <RateItem item={item} onRated={refetch} />

      <Evidence item={item} />

      {needsValue && (
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={
            item.type === "lookup"
              ? "Street address"
              : item.type === "family"
                ? "Canonical company name"
                : "Company to print on the card"
          }
          className="font-mono"
        />
      )}

      <div className="flex gap-2 flex-wrap">
        {choices.map((c) => (
          <Button
            key={c.key}
            variant={
              c.tone === "destructive"
                ? "destructive"
                : c.tone === "outline"
                  ? "outline"
                  : "default"
            }
            disabled={saving}
            onClick={() => submit(c)}
          >
            <span className="font-mono text-xs opacity-60 mr-2">{c.key}</span>
            {c.label}
          </Button>
        ))}
        <Button
          variant="ghost"
          disabled={saving}
          onClick={() => setCursor((c) => Math.min(c + 1, items.length - 1))}
        >
          <span className="font-mono text-xs opacity-60 mr-2">s</span>
          Skip
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        Keys: <b>1</b>/<b>2</b> answer · <b>3</b> not a distillery · <b>s</b>{" "}
        skip · <b>j</b>/<b>k</b> or arrows move ·
        {needsValue ? " Enter submits from the field" : " no typing needed"}
      </p>
    </div>
  );
}
